import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Badge,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Keyboard as KeyboardIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { useIsMobile } from '../../../../hooks/useResponsive';
import SearchBar from './SearchBar';
import TicketTable from './TicketTable';
import TicketMobileCards from './TicketMobileCards';
import FilterDrawer from './FilterDrawer';
import FilterChips from './FilterChips';
import { TicketTableSkeleton, TicketCardSkeleton } from '../shared/TicketSkeleton';
import TicketEmptyState from '../shared/TicketEmptyState';

/**
 * Main list view container for tickets
 */
const TicketListView = ({
  tickets,
  totalTickets,
  loading,
  error,
  filter,
  page,
  rowsPerPage,
  orderBy,
  order,
  searchInputValue,
  hasActiveFilters,
  filterDrawerOpen,
  locations,
  onFilterDrawerToggle,
  onFilterChange,
  onResetFilter,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onViewTicket,
  onEditTicket,
  onCreateTicket,
  onRefresh,
  onExportPdf
}) => {
  const { t } = useThemeSettings();
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();

  const handleClearSearch = () => {
    onSearchChange({ target: { value: '' } });
  };

  // Count active filters for badge
  const activeFilterCount =
    filter.status.length +
    (filter.urgency ? 1 : 0) +
    (filter.support_type ? 1 : 0) +
    (filter.next_step ? 1 : 0);

  if (loading) {
    return isMobile ? <TicketCardSkeleton count={5} /> : <TicketTableSkeleton rows={10} />;
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={onRefresh} sx={{ mt: 2 }}>
          {t ? t('Common.retry') : 'Retry'}
        </Button>
      </Paper>
    );
  }

  // Determine empty state variant
  const getEmptyStateVariant = () => {
    if (searchInputValue) return 'no-results';
    if (hasActiveFilters) return 'no-filter-results';
    return 'no-data';
  };

  return (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between'
        }}>
          {/* Search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <SearchBar
              value={searchInputValue}
              onChange={onSearchChange}
              onClear={handleClearSearch}
              placeholder={t ? t('Tickets.search_placeholder') : 'Search tickets...'}
            />
            {!isMobile && (
              <Tooltip title={t ? t('Common.keyboard_shortcuts') : 'Keyboard shortcuts'}>
                <Chip
                  icon={<KeyboardIcon fontSize="small" />}
                  label="?"
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'default', opacity: 0.7 }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title={`${t ? t('Tickets.filter') : 'Filter'} (F)`}>
              <IconButton onClick={onFilterDrawerToggle}>
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  invisible={activeFilterCount === 0}
                >
                  <FilterIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={`${t ? t('Tickets.refresh') : 'Refresh'} (R)`}>
              <IconButton onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {onExportPdf && (
              <Tooltip title={t ? t('Tickets.export_pdf') : 'Export PDF'}>
                <IconButton onClick={onExportPdf}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
            )}

            {hasPermission('tickets.create') && (
              <Tooltip title={`${t ? t('Tickets.create_ticket') : 'Create Ticket'} (C)`}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onCreateTicket}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {isMobile ? (t ? t('Common.new') : 'New') : (t ? t('Tickets.create_ticket') : 'Create Ticket')}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Keyboard shortcuts hint - only on desktop */}
        {!isMobile && (
          <Box sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            opacity: 0.6,
            fontSize: '0.75rem'
          }}>
            <Typography variant="caption" color="text.secondary">
              <strong>C</strong> {t ? t('Tickets.create_new') : 'New'} •{' '}
              <strong>/</strong> {t ? t('Common.search') : 'Search'} •{' '}
              <strong>F</strong> {t ? t('Tickets.filter') : 'Filter'} •{' '}
              <strong>R</strong> {t ? t('Tickets.refresh') : 'Refresh'} •{' '}
              <strong>P</strong> {t ? t('Tickets.export_pdf') : 'PDF'} •{' '}
              <strong>↑↓</strong> {t ? t('Common.navigate') : 'Navigate'} •{' '}
              <strong>Enter</strong> {t ? t('Common.open') : 'Open'} •{' '}
              <strong>?</strong> {t ? t('Common.help') : 'Help'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Filter Chips */}
      {hasActiveFilters && (
        <FilterChips
          filter={filter}
          onFilterChange={onFilterChange}
          onResetFilter={onResetFilter}
        />
      )}

      {/* Content */}
      {tickets.length === 0 ? (
        <TicketEmptyState
          variant={getEmptyStateVariant()}
          hasCreatePermission={hasPermission('tickets.create')}
          onCreateTicket={onCreateTicket}
          onClearFilters={onResetFilter}
          onClearSearch={handleClearSearch}
          searchTerm={searchInputValue}
          filterCount={activeFilterCount}
        />
      ) : isMobile ? (
        <TicketMobileCards
          tickets={tickets}
          totalTickets={totalTickets}
          page={page}
          rowsPerPage={rowsPerPage}
          locations={locations}
          onPageChange={onPageChange}
          onViewTicket={onViewTicket}
          onEditTicket={onEditTicket}
        />
      ) : (
        <TicketTable
          tickets={tickets}
          totalTickets={totalTickets}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          order={order}
          locations={locations}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          onSort={onSort}
          onViewTicket={onViewTicket}
          onEditTicket={onEditTicket}
        />
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={onFilterDrawerToggle}
        filter={filter}
        onFilterChange={onFilterChange}
        onResetFilter={onResetFilter}
      />
    </Box>
  );
};

export default TicketListView;
